import type React from "react";
import { Badge, Box, Button, Center, Group, Loader, Progress, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import {
  JobStatus,
  type JobViewModel,
  type RowEdit,
} from "@models/job";
import { isOeRow, isTmRow } from "@models/mappingResult";
import type { MappingResultRow, TmVariant } from "@models/mappingResult";
import type {
  RowSaveStatus,
  UseJobResultsState,
} from "@hooks/useJobResults";
import WdJobResultsTable from "./table/wd-mapping/WdJobResultsTable";
import TmJobResultsTable from "./table/technical-manual/TmJobResultsTable";
import TmBatchCompare from "./table/technical-manual/TmBatchCompare";
import SourcePdfLink from "./SourcePdfLink";
import ResultsFilterChips from "./table/ResultsFilterChips";
import {
  ResultTableVariant,
  resolveResultTableVariant,
} from "@utils/resultTableVariant";
import type { ReviewStatusFilter } from "@utils/jobResultsHelpers";
import { variantIdentity } from "@utils/variantLabel";

interface JobResultsBodyProps {
  job: JobViewModel | null;
  jobId: string;
  showResults: boolean;
  results: UseJobResultsState;
  filteredRows: MappingResultRow[];
  statusFilter: ReviewStatusFilter;
  onStatusFilterChange: (value: ReviewStatusFilter) => void;
  statusCounts: Record<ReviewStatusFilter, number>;
  priorityOnly: boolean;
  onPriorityOnlyChange: (value: boolean) => void;
  priorityCount: number;
  missingOnly: boolean;
  onMissingOnlyChange: (value: boolean) => void;
  missingCount: number;
  edits: Map<number, RowEdit>;
  saveStates: Map<number, RowSaveStatus>;
  /** Keyboard-focused row: highlighted by the default (generic) table only. */
  registerRowRef: (rowIndex: number, el: HTMLTableRowElement | null) => void;
  onAccept: (rowIndex: number, text?: string) => void;
  onReject: (rowIndex: number) => void;
  onRecover: (rowIndex: number) => void;
  onAcceptAll: (rowIndexes: number[]) => Promise<void>;
  onRejectAll: (rowIndexes: number[]) => Promise<void>;
  onRetrySave: (rowIndex: number) => void;
  onRowRestored: (row: MappingResultRow) => void;
  onLoadMore: () => void;
  onAddRow: (
    itemMpId: number,
    itemMpText: string,
    value: string,
    variant: TmVariant | null,
  ) => Promise<void>;
  batchView: "compare" | "review";
  differentOnly: boolean;
}

const JobResultsBody: React.FC<JobResultsBodyProps> = ({
  job,
  jobId,
  showResults,
  results,
  filteredRows,
  statusFilter,
  onStatusFilterChange,
  statusCounts,
  priorityOnly,
  onPriorityOnlyChange,
  priorityCount,
  missingOnly,
  onMissingOnlyChange,
  missingCount,
  edits,
  saveStates,
  registerRowRef,
  onAccept,
  onReject,
  onRecover,
  onAcceptAll,
  onRejectAll,
  onRetrySave,
  onRowRestored,
  onLoadMore,
  onAddRow,
  batchView,
  differentOnly,
}) => {
  const { t } = useTranslation();
  const variantsByIdentity = new Map<string, TmVariant>();
  for (const row of results.rows) {
    if (!isTmRow(row)) continue;
    const variant = row.source.variant;
    if (variant === null || (typeof variant === "string" && !variant.trim())) continue;
    const identity = variantIdentity(variant);
    if (!variantsByIdentity.has(identity) || typeof variant !== "string") {
      variantsByIdentity.set(identity, variant);
    }
  }
  const tmVariants = [...variantsByIdentity.values()];
  const files = job?.files ?? [];
  const isTechnicalManual =
    resolveResultTableVariant(job?.pluginId) === ResultTableVariant.TechnicalManual;
  const isBatchTm = files.length > 1 &&
    resolveResultTableVariant(job?.pluginId) !== ResultTableVariant.Wd;
  const comparing = isBatchTm && batchView === "compare";

  const renderResultTable = (): React.ReactNode => {
    switch (resolveResultTableVariant(job?.pluginId)) {
      case ResultTableVariant.TechnicalManual:
        return (
          <TmJobResultsTable
            rows={filteredRows.filter(isTmRow)}
            files={files}
            edits={edits}
            saveStates={saveStates}
            jobId={jobId}
            registerRowRef={registerRowRef}
            onAccept={onAccept}
            onReject={onReject}
            onRecover={onRecover}
            onRetrySave={onRetrySave}
            onAcceptAll={onAcceptAll}
            onRejectAll={onRejectAll}
            hasMore={results.hasMore}
            loadingMore={results.loadingMore}
            onLoadMore={onLoadMore}
            variants={tmVariants}
            onAddRow={onAddRow}
          />
        );
      case ResultTableVariant.Wd:
        return (
          <WdJobResultsTable
            rows={filteredRows.filter(isOeRow)}
            edits={edits}
            saveStates={saveStates}
            jobId={jobId}
            registerRowRef={registerRowRef}
            onAccept={onAccept}
            onReject={onReject}
            onRecover={onRecover}
            onRetrySave={onRetrySave}
            onRowRestored={onRowRestored}
            hasMore={results.hasMore}
            loadingMore={results.loadingMore}
            onLoadMore={onLoadMore}
          />
        );
      // A plugin the map does not know still returns rows tagged `tm` — the
      // backend's base service builds the TM shape for an unregistered
      // service, so that is what the fallback has to render. There is no
      // generic row shape on the wire any more to render generically.
      default:
        return (
          <TmJobResultsTable
            rows={filteredRows.filter(isTmRow)}
            files={files}
            edits={edits}
            saveStates={saveStates}
            jobId={jobId}
            registerRowRef={registerRowRef}
            onAccept={onAccept}
            onReject={onReject}
            onRecover={onRecover}
            onRetrySave={onRetrySave}
            onAcceptAll={onAcceptAll}
            onRejectAll={onRejectAll}
            hasMore={results.hasMore}
            loadingMore={results.loadingMore}
            onLoadMore={onLoadMore}
            variants={tmVariants}
            onAddRow={onAddRow}
          />
        );
    }
  };

  return (
    <Box className="job-results-page__body">
      {job?.error && (
        <Box className="job-results-page__job-meta">
          <Text size="sm" c="red">
            {job.error.message}
          </Text>
        </Box>
      )}
      {/* The technical-manual table names each row's PDF and counts them in its
          Source PDF header, so it has no per-file card. */}
      {files.length > 0 && !comparing && !isTechnicalManual && (
        <Box className="job-results-page__job-meta" aria-label={t("jobResultsPage.files.label", { defaultValue: "Files" })}>
          <Stack gap="xs">
            {files.map((file) => (
              <Group key={file.fileIndex} align="flex-start" gap="xs">
                <SourcePdfLink jobId={jobId} fileIndex={file.fileIndex} filename={file.memberPath ?? file.filename} hasInputPdf={file.hasInputPdf} />
                {file.status !== "Completed" && (
                  <Badge size="sm" color={file.status === "Failed" ? "red" : "blue"}>
                    {t(`mappingServicePage.statuses.${file.status}`, { defaultValue: file.status })}
                  </Badge>
                )}
                {file.rowsTotal > 0 && (
                  <Text size="xs" c="dimmed">
                    {t("mappingServicePage.main.rowsCount", {
                      done: file.rowsOk + file.rowsFailed,
                      total: file.rowsTotal,
                    })}
                  </Text>
                )}
                {file.sourceUnits && (
                  <Text size="xs" c="dimmed">
                    {file.sourceUnits.done} / {file.sourceUnits.total} {file.sourceUnits.kind}
                  </Text>
                )}
                {file.error && <Text size="sm" c="red" role="alert">{file.error.message}</Text>}
              </Group>
            ))}
          </Stack>
        </Box>
      )}

      {showResults && !comparing && results.rows.length > 0 && (
        <ResultsFilterChips
          value={statusFilter}
          onChange={onStatusFilterChange}
          counts={statusCounts}
          priorityOnly={priorityOnly}
          onPriorityOnlyChange={onPriorityOnlyChange}
          priorityCount={priorityCount}
          missingOnly={missingOnly}
          onMissingOnlyChange={onMissingOnlyChange}
          missingCount={missingCount}
        />
      )}

      {!showResults && job && job.status !== JobStatus.Failed && (
        <Center mih={200}>
          <Stack align="center" gap="xs">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              {t("mappingServicePage.main.waitingForRows")}
            </Text>
          </Stack>
        </Center>
      )}

      {showResults && results.loading && (
        <Center mih={200}>
          <Stack align="center" gap="xs">
            <Loader size="sm" />
            <Text size="sm">
              {t("mappingServicePage.main.loadingResults")}
            </Text>
          </Stack>
        </Center>
      )}

      {showResults && results.notReady && (
        <Center mih={200}>
          <Stack align="center" gap="xs" w={320} maw="80%">
            <Text size="sm">
              {t("mappingServicePage.main.processingRows")}
            </Text>
            {job && job.rowsTotal ? (
              <>
                <Progress
                  w="100%"
                  value={Math.min(100, Math.round(((job.rowsOk + job.rowsFailed) / job.rowsTotal) * 100))}
                  transitionDuration={400}
                />
                <Text size="xs" c="dimmed">
                  {t("mappingServicePage.main.rowsCount", {
                    done: job.rowsOk + job.rowsFailed,
                    total: job.rowsTotal,
                  })}
                </Text>
              </>
            ) : (
              <>
                <Loader size="sm" />
                <Text size="xs" c="dimmed">
                  {t("mappingServicePage.main.resultsNotReady")}
                </Text>
              </>
            )}
          </Stack>
        </Center>
      )}

      {showResults &&
        !results.loading &&
        !results.error &&
        results.rows.length === 0 &&
        !results.notReady && (
          <Center mih={200}>
            <Text size="sm" c="dimmed">
              {t("mappingServicePage.main.noRows")}
            </Text>
          </Center>
        )}

      {showResults && !comparing &&
        results.rows.length > 0 &&
        filteredRows.length === 0 && (
          <Center mih={120}>
            <Text size="sm" c="dimmed">
              {t("jobResultsPage.filters.noMatches")}
            </Text>
          </Center>
        )}

      {showResults && !comparing && filteredRows.length > 0 && renderResultTable()}
      {showResults && comparing && results.rows.length > 0 && !results.loading && (
        results.hasMore ? (
          <Center mih={160}>
            <Stack align="center" gap="xs">
              {results.error ? (
                <>
                  <Text size="sm" c="red">{results.error}</Text>
                  <Button variant="light" onClick={onLoadMore}>{t("jobResultsPage.batchCompare.retry")}</Button>
                </>
              ) : (
                <>
                  <Loader size="sm" />
                  <Text size="sm">{t("jobResultsPage.batchCompare.loading")}</Text>
                </>
              )}
            </Stack>
          </Center>
        ) : (
          <TmBatchCompare
            key={jobId}
            jobId={jobId}
            rows={results.rows.filter(isTmRow)}
            files={files}
            edits={edits}
            differentOnly={differentOnly}
          />
        )
      )}
    </Box>
  );
};

export default JobResultsBody;
